import { useState, useCallback, useEffect, useRef } from 'react';

import { apiFetch } from '@/lib/api';
import type { Job } from '@/types/forensics';

interface UseJobListReturn {
  jobs: Job[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  deleteJob: (jobId: string) => Promise<void>;
}

export function useJobList(): UseJobListReturn {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchJobs = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const response = await apiFetch('/api/jobs');
      if (!response.ok) throw new Error('Failed to fetch jobs');
      const data = await response.json();
      setJobs(data.jobs ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteJob = useCallback(
    async (jobId: string): Promise<void> => {
      try {
        const response = await apiFetch(`/api/jobs/${jobId}`, { method: 'DELETE' });
        if (!response.ok) throw new Error('Failed to delete job');
        await fetchJobs();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete job');
      }
    },
    [fetchJobs],
  );

  useEffect(() => {
    fetchJobs();

    intervalRef.current = setInterval(fetchJobs, 10000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchJobs]);

  return { jobs, loading, error, refetch: fetchJobs, deleteJob };
}
